/**
 * Hooks TanStack Query — publications multi-plateforme (S6 PR2-D quater
 * + lifecycle PR2-C).
 *
 * - `useWorkPublications({ workId, platform, pollMs? })` : lecture des
 *   publications d'une œuvre pour une plateforme donnée. Le `pollMs`
 *   optionnel active un refetchInterval — utile juste après un enqueue
 *   pour voir la publication apparaître dès que le scheduler a fini son
 *   tick (~30s max).
 * - `useEnqueuePublication({ workId, platform })` : mutation POST publish.
 * - `useUpdatePublication({ workId, platform })` : mutation PATCH lifecycle
 *   (textes draft + diff add/remove mockups).
 * - `useEndPublication({ workId, platform })` : mutation DELETE lifecycle
 *   (retire la publication active, reason ∈ {user_unpublish, sold_direct}).
 * - `useRenewPublication({ workId, platform })` : mutation POST /renew
 *   (boost SEO, 0,20 € sur Etsy).
 * - `useDeleteWithReconciliation({ workId, platform })` : mutation POST
 *   /delete — suppression intelligente avec probe plateforme préalable.
 *   Le BE bascule `state='deleted'` directement si l'API plateforme
 *   renvoie 404 (listing supprimé manuellement hors-app), sinon enqueue
 *   un `delete_publication` job (le scheduler fait le DELETE plateforme).
 *
 * Toutes les mutations invalident `workPublicationsKeys.forWork(workId,
 * platform)` au succès — le caller (`<PlatformPublishCard>`) re-fetch
 * automatiquement la liste avec l'état post-job.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { workPublicationsApi } from '@/utils/api/work-publications'
import type { components } from '@/types/api-generated'
import type {
  Platform,
  PlatformPublishEnqueueResponse,
  PlatformPublishRequest,
  PlatformResourceResponse,
} from '@/types/publications'
import type {
  DeleteWithReconciliationResponse,
  EndPublicationReason,
  PinterestMarkDuplicatesResponse,
} from '@/utils/api/work-publications'

type PlatformPublicationUpdateRequest = components['schemas']['PlatformPublicationUpdateRequest']
type PlatformPublicationLifecycleResponse =
  components['schemas']['PlatformPublicationLifecycleResponse']

interface ApiError {
  detail: string
  status?: number
}

export const workPublicationsKeys = {
  all: ['work-publications'] as const,
  forWork: (workId: number, platform: Platform) =>
    [...workPublicationsKeys.all, 'work', workId, platform] as const,
}

interface UseWorkPublicationsArgs {
  workId: number | null | undefined
  platform: Platform
  pollMs?: number
}

export function useWorkPublications({ workId, platform, pollMs }: UseWorkPublicationsArgs) {
  return useQuery<PlatformResourceResponse[]>({
    queryKey: workPublicationsKeys.forWork(workId ?? -1, platform),
    queryFn: () => workPublicationsApi.list(workId as number, platform),
    enabled: workId !== null && workId !== undefined && workId > 0,
    refetchInterval: pollMs ?? false,
  })
}

interface UseLifecycleArgs {
  workId: number
  platform: Platform
}

export function useEnqueuePublication({ workId, platform }: UseLifecycleArgs) {
  const qc = useQueryClient()
  return useMutation<PlatformPublishEnqueueResponse, ApiError, PlatformPublishRequest>({
    mutationFn: (payload) => workPublicationsApi.enqueuePublish(workId, platform, payload),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, platform),
      })
    },
  })
}

interface UpdatePublicationVars {
  resourceId: number
  payload: PlatformPublicationUpdateRequest
}

export function useUpdatePublication({ workId, platform }: UseLifecycleArgs) {
  const qc = useQueryClient()
  return useMutation<PlatformPublicationLifecycleResponse, ApiError, UpdatePublicationVars>({
    mutationFn: ({ resourceId, payload }) =>
      workPublicationsApi.updatePublication(workId, platform, resourceId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, platform),
      })
    },
  })
}

interface EndPublicationVars {
  resourceId: number
  reason: EndPublicationReason
}

export function useEndPublication({ workId, platform }: UseLifecycleArgs) {
  const qc = useQueryClient()
  return useMutation<PlatformPublicationLifecycleResponse, ApiError, EndPublicationVars>({
    mutationFn: ({ resourceId, reason }) =>
      workPublicationsApi.endPublication(workId, platform, resourceId, reason),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, platform),
      })
    },
  })
}

interface RenewPublicationVars {
  resourceId: number
}

export function useRenewPublication({ workId, platform }: UseLifecycleArgs) {
  const qc = useQueryClient()
  return useMutation<PlatformPublicationLifecycleResponse, ApiError, RenewPublicationVars>({
    mutationFn: ({ resourceId }) =>
      workPublicationsApi.renewPublication(workId, platform, resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, platform),
      })
    },
  })
}

interface DeleteWithReconciliationVars {
  resourceId: number
}

export function useDeleteWithReconciliation({ workId, platform }: UseLifecycleArgs) {
  const qc = useQueryClient()
  return useMutation<DeleteWithReconciliationResponse, ApiError, DeleteWithReconciliationVars>({
    mutationFn: ({ resourceId }) =>
      workPublicationsApi.deleteWithReconciliation(workId, platform, resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, platform),
      })
    },
  })
}

/**
 * S7-PR2 — Marque N Pins Pinterest comme supprimés côté atelier
 * (local-only). Invalide la liste Pinterest + le journal cross-plateforme
 * du work pour refléter immédiatement les bascules `state='deleted'`.
 */
export function useMarkPinterestDuplicatesDeleted(workId: number) {
  const qc = useQueryClient()
  return useMutation<PinterestMarkDuplicatesResponse, ApiError, number[]>({
    mutationFn: (resourceIds) =>
      workPublicationsApi.markPinterestDuplicatesDeleted(workId, resourceIds),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(workId, 'pinterest'),
      })
      // Le journal cross-plateforme vit dans une autre clé — l'invalider
      // aussi pour que le `<WorkPublicationsTab>` re-fetch et n'affiche
      // plus les rows marquées `deleted` dans la liste "actives".
      void qc.invalidateQueries({
        queryKey: ['work-publications-journal', 'work', workId],
      })
    },
  })
}
